// web/src/types/scan.ts - Scan & ScanConfig types

import type { Project } from './project';
import type { Target } from './target';
import type { ScanResult } from './finding';
import type { Agent } from './agent';

export interface ScanConfig {
  // Basic scanning options
  port_scan: boolean;
  service_detection: boolean;
  os_detection: boolean;
  screenshots: boolean;
  shares_enum: boolean;
  web_scan: boolean;
  ssl_scan: boolean;
  vuln_scan: boolean;
  retirejs_scan: boolean;
  dns_scan: boolean;
  osint_scan: boolean;
  wordpress_scan: boolean;

  // WordPress scanning configuration - NEW
  wordpress_timeout: number;
  wordpress_min_severity: string;
  wordpress_check_core: boolean;
  wordpress_check_plugins: boolean;
  wordpress_check_themes: boolean;
  wordpress_aggressive_detection: boolean;
  wordpress_enumerate_users: boolean;
  wordpress_check_config: boolean;
  wordpress_ports: string;

  ports: string;
  threads: number;
  timeout: number;

  // Main Port Discovery configuration
  top_ports: number;        // Number of top ports to scan (default 3000)
  additional_ports: string; // Additional ports for pass 2 (security-relevant ports not in top 3000)

  // UDP Scanning configuration
  enable_udp_scan: boolean;   // Enable UDP port scanning pass
  udp_top_ports: number;      // Number of top UDP ports to scan (default 100)
  udp_custom_ports: string;   // Custom UDP ports to scan

  // Screenshot-specific options
  screenshot_viewport: boolean;
  screenshot_ports: string; // 'short', 'default', or 'custom'
  screenshot_custom_ports: string; // Custom port list if screenshot_ports='custom'
  screenshot_timeout: number; // Screenshot timeout in seconds

  // Advanced nmap options
  stealth_scan: boolean;
  ping_scan: boolean;
  script_scan: boolean;
  vulners_script: boolean;
  http_cookie_flags: boolean; // NEW: HTTP Cookie Flags Analysis
  intense_scan: boolean;
  aggressive_timing: boolean;
  no_ping: boolean;
  fragment_packets: boolean;
  decoy_scan: boolean;
  version_intensity: number;
  custom_args: string;
  custom_args_all_scans: boolean; // NEW: Apply custom args to all nmap scans (not just port scan)

  // Vulnerability scanning options - ENHANCED
  vuln_enabled: boolean;
  vuln_sources: string; // 'cve', 'custom', 'cve,custom', etc.
  vuln_min_severity: string; // 'info', 'low', 'medium', 'high', 'critical'
  vuln_match_mode: string; // 'strict', 'loose', 'fuzzy'
  vuln_include_expired: boolean;
  vuln_custom_rules_only: boolean;
  enable_exploitdb_correlation: boolean; // Opt-in: correlate detected versions against the ExploitDB catalogue (~46k rules, noisier)

  // Logging and output options
  enable_logging: boolean;
  log_level: 'debug' | 'info' | 'warn' | 'error';
  save_raw_output: boolean;

  // Performance and resource limits
  max_concurrent_hosts: number;
  max_scan_time: number;

  // Web scanning specific options
  web_max_pages: number;
  web_follow_redirects: boolean;
  web_user_agent: string;
  web_timeout: number;
  web_quick_mode: boolean;

  // Share enumeration options
  shares_timeout: number;
  shares_deep_scan: boolean;
  shares_test_write: boolean;

  // SSL/TLS scanning options
  ssl_test_all_ciphers: boolean;
  ssl_check_certs: boolean;
  ssl_test_compression: boolean;

  // Output and reporting options
  export_formats: string;
  include_screenshots: boolean;
  report_template: string;
  // OSINT scanning configuration - NEW
  osint_enabled: boolean;
  osint_subdomain_discovery: boolean;
  osint_wildcard_detection: boolean;
  osint_create_targets: boolean;
  osint_max_subdomains: number;
  osint_timeout: number;
  osint_rate_limit: number;
  osint_sources: string[];
  osint_passive_only: boolean;
  osint_include_expired: boolean;
  osint_min_confidence: number;
  osint_dns_brute_force: boolean;
  osint_brute_wordlist: string;
  osint_ptr_analysis: boolean;
  osint_zone_transfer: boolean;
  osint_certificate_transparency: boolean;
  osint_search_engines: boolean;
  osint_social_media: boolean;
  osint_code_repositories: boolean;
  osint_threat_intelligence: boolean;
  osint_custom_apis: string[];
  osint_output_format: string;
  osint_save_raw_data: boolean;

  // Nuclei engine configuration - NEW
  nuclei_enabled: boolean;
  nuclei_templates: string[];
  nuclei_categories: string[];
  nuclei_severities: string[];
  nuclei_tags: string[];
  nuclei_concurrency: number;
  nuclei_rate_limit: number;
  nuclei_use_detected_tech: boolean;
  nuclei_custom_templates: boolean;
  nuclei_template_path: string;
  nuclei_exclude_templates: string[];
  nuclei_include_templates: string[];
  nuclei_passive_scan: boolean;
  nuclei_follow_redirects: boolean;
  nuclei_max_redirects: number;
  nuclei_timeout: number;
  nuclei_retries: number;
  nuclei_bulk_size: number;
  nuclei_template_threads: number;
  nuclei_headless: boolean;
  nuclei_system_resolvers: boolean;
  nuclei_disable_clustering: boolean;
  nuclei_offline_scan: boolean;
  nuclei_scan_all_ips: boolean;
  nuclei_ip_version: string; // '4', '6', 'auto'
  nuclei_interface: string;
  nuclei_source_ip: string;
  nuclei_config_file: string;
  nuclei_report_config: string;
  nuclei_severity_threshold: string;
  nuclei_exclude_severity: string[];
  nuclei_exclude_protocols: string[];
  nuclei_exclude_tags: string[];
  nuclei_include_tags: string[];
  nuclei_include_ids: string[];
  nuclei_exclude_ids: string[];
  nuclei_author: string[];
  nuclei_attack_type: string[];
  nuclei_protocol: string[];
  nuclei_exclude_matchers: string[];
  nuclei_include_conditions: string[];
  nuclei_stop_at_first_match: boolean;
  nuclei_exclude_type: string[];
  nuclei_include_type: string[];
  nuclei_template_display: boolean;
  nuclei_template_list: boolean;
  nuclei_silent: boolean;
  nuclei_verbose: boolean;
  nuclei_very_verbose: boolean;
  nuclei_debug: boolean;
  nuclei_debug_requests: boolean;
  nuclei_debug_response: boolean;
  nuclei_proxy_url: string;
  nuclei_proxy_socks_url: string;
  nuclei_custom_headers: string[];
  nuclei_custom_attack_type: string;
  nuclei_random_agent: boolean;
  nuclei_disable_redirects: boolean;
  nuclei_report_format: string[];
  nuclei_report_db: string;
  nuclei_markdown_export: string;
  nuclei_sarif_export: string;
  nuclei_json_export: string;
  nuclei_jsonl_export: string;
  nuclei_junit_export: string;
  nuclei_csv_export: string;
  nuclei_disk_export: string;
  nuclei_resume: string;
  nuclei_stream_logs: boolean;
  nuclei_no_meta: boolean;
  nuclei_no_timestamp: boolean;
  nuclei_env_vars: boolean;
  nuclei_max_host_error: number;
  nuclei_track_error: string[];
  nuclei_flush_interval: number;
  nuclei_stats_interval: number;
  nuclei_metrics: boolean;
  nuclei_metrics_port: number;
  nuclei_no_color: boolean;
  nuclei_no_update_templates: boolean;
  nuclei_no_templates: boolean;
  nuclei_validate: boolean;
  nuclei_validate_export: string;

  // Advanced Web Vulnerability Scanning
  advanced_web_scan: boolean;
  sqli_scan: boolean;
  xss_scan: boolean;
  open_redirect_scan: boolean;
  ssrf_scan: boolean;

  // Network Protocol Scanning
  snmp_scan: boolean;
  ldap_scan: boolean;
  database_scan: boolean;
  container_scan: boolean;
  default_creds_scan: boolean;
  ipmi_scan: boolean;
  rdp_scan: boolean;             // RDP vulnerability scanning (BlueKeep, NLA bypass)
  protocol_sec_scan: boolean;    // Cleartext protocol detection (FTP, Telnet, etc.)
  solr_scan: boolean;            // Apache Solr vulnerability scanning
  clamav_scan: boolean;          // ClamAV misconfiguration scanning
  php_version_scan: boolean;     // PHP version detection and CVE matching
  os_eol_scan: boolean;          // OS End-of-Life detection

  // Additional Web Security Scanning
  cms_scan: boolean;             // CMS detection and vulnerability scanning
  jwt_scan: boolean;             // JWT token analysis and vulnerability detection
  secrets_scan: boolean;         // Exposed secrets detection (API keys, credentials, tokens)

  // ICS/OT Scanning
  ot_scan: boolean;              // ICS/SCADA/OT scanning master toggle

  // Hostname alias scanning behaviour (per scan/schedule)
  alias_scan_sequential?: boolean; // true = scan same-IP hostname aliases one-at-a-time; default false = parallel

  // OAST (Out-of-Band Application Security Testing)
  oast_enabled: boolean;         // Enable OAST for blind vulnerability detection
  oast_server_url?: string;      // OAST server URL (default: oast.exposiq.ch)

  // Active Exploit Validation (INTRUSIVE - disabled by default)
  exploit_validation_enabled: boolean; // Run active CVE exploit validation
  exploit_validation_mode: 'safe' | 'full'; // safe = exclude dangerous exploits, full = all exploits
  dangerous_exploit_consent: boolean; // User confirmed backups/rollbacks in place
  dangerous_exploit_consent_at?: string; // ISO timestamp when consent was given
  dangerous_exploit_consent_by?: string; // Username who gave consent

  // API Vulnerability Scanning
  api_scan_enabled?: boolean; // Enable API vulnerability scanning
  api_openapi_url?: string; // URL to OpenAPI/Swagger specification
  api_auth_token?: string; // Bearer token for authenticated API testing
  api_auth_header?: string; // Custom auth header name (default: Authorization)
  api_base_path?: string; // Base path override (e.g., /api/v2)
  api_graphql_enabled?: boolean; // Enable GraphQL-specific testing
  api_rate_limit_test?: boolean; // Enable rate limit testing (sends burst traffic)
}

export interface Scan {
  id: number;
  project_id: number;
  name: string;
  description: string;
  status: 'pending' | 'running' | 'paused' | 'completed' | 'failed' | 'stopped' | 'cancelled' | 'interrupted';
  progress: number;
  started_at?: string;
  completed_at?: string;
  config: ScanConfig;
  created_at: string;
  updated_at: string;
  project?: Project;
  targets: Target[];
  results: ScanResult[];
  results_count: number; // Total count of results (computed field)
  log_file?: string; // Path to scan log file
  // Scan type (vulnerability, discovery, recon)
  scan_type?: 'vulnerability' | 'discovery' | 'recon';
  // Agent support
  agent_id?: string;      // Agent ID if scan runs via agent
  is_agent_scan?: boolean; // True if scan ran via agent
  command_id?: string;    // Command ID for agent-based scans
  agent?: Agent;          // Agent details (populated by API)
  // Severity counts (from API)
  critical_count?: number;
  high_count?: number;
  medium_count?: number;
  low_count?: number;
  info_count?: number;
  // Risk grade (A-F) calculated after scan completion
  risk_grade?: string;      // A, B, C, D, E, F
  risk_score?: number;      // Numeric score 0-100
  risk_grade_calc_at?: string; // When grade was calculated
}

// WebSocket message types
export interface ScanProgress {
  scan_id: number;
  overall_progress: number;
  status: ScanStatus;
  message: string;
  live_results?: LiveResult[];
  results_stats: ResultsStats;
}

export interface ScanStatus {
  currentTarget: string;
  currentEngine: string;
  targetsTotal: number;
  targetsComplete: number;
  engineStatus: Record<string, EngineStatus>;
  isPaused: boolean;
  startTime: string;
  lastUpdate: string;
  // Hostname alias scanning progress
  currentAlias?: string;
  currentAliasIP?: string;
  aliasesTotal?: number;
  aliasesCompleted?: number;
}

export interface LiveResult {
  id: number;
  host: string;
  hostname?: string;
  port: number;
  title: string;
  severity: string;
  vuln_type: string;
  timestamp: string;
}

export interface ResultsStats {
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
}

export type EngineStatus = 'idle' | 'running' | 'completed' | 'failed';

// Extended interfaces for frontend components
export interface ScanWithProject extends Scan {
  project_name: string;
}

export interface ScanWithResults extends Scan {
  project_name: string;
  results_summary: {
    total: number;
    critical: number;
    high: number;
    medium: number;
    low: number;
    info: number;
  };
}

// Filter types
export interface ScanFilters {
  status: string[];
  project_id?: number;
  search: string;
}

// Form types
export interface CreateScanForm {
  name: string;
  description: string;
  config: ScanConfig;
  target_ids: number[];
}

// WebSocket event types
export interface WebSocketMessage {
  type: 'scan_update' | 'scan_progress' | 'scan_completion' | 'scan_failed' | 'scan_paused' | 'scan_resumed' | 'scan_stopped' | 'vuln_import_progress';
  data: any;
}

export interface ScanUpdateEvent {
  type: 'scan_update';
  scan_id: number;
  status: string;
  message: string;
  progress?: number;
  timestamp: string;
}

export interface ScanProgressEvent {
  type: 'scan_progress';
  scan_id: number;
  progress: ScanProgress;
  timestamp: string;
}

// Scan Templates - preset configurations for common scan types
export type ScanTemplateId = 'external' | 'internal' | 'webapp' | 'fast_agent' | 'quick' | 'monitor' | 'recon';

export interface ScanTemplate {
  id: ScanTemplateId;
  icon: string;
  color: string;
  titleKey: string;
  descriptionKey: string;
  highlightKeys: string[];
  configOverrides: Partial<ScanConfig>;
}

// Constants
export const SCAN_STATUSES = ['pending', 'running', 'paused', 'completed', 'failed', 'stopped', 'cancelled'] as const;

// Type guards
export const isScanStatus = (status: string): status is Scan['status'] => {
  return SCAN_STATUSES.includes(status as any);
};

// Default values
export const DEFAULT_SCAN_FILTERS: ScanFilters = {
  status: [],
  search: '',
};

export const DEFAULT_SCAN_CONFIG: ScanConfig = {
  // Basic scanning options - Updated based on screenshots
  port_scan: true,
  service_detection: true,
  os_detection: true,
  screenshots: true,  // Changed to true (enabled in screenshot)
  shares_enum: false, // Share Enumeration is UNCHECKED in screenshot
  web_scan: true,     // Changed to true (enabled in screenshot)
  ssl_scan: true,     // Changed to true (enabled in screenshot)
  vuln_scan: true,    // Changed to true (enabled in screenshot)
  retirejs_scan: true, // Changed to true (enabled in screenshot)
  dns_scan: true,     // Changed to true (enabled in screenshot)
  osint_scan: true,   // Changed to true (enabled in screenshot)
  wordpress_scan: true, // Changed to true (enabled in screenshot)

  // WordPress scanning configuration - NEW
  wordpress_timeout: 30,
  wordpress_min_severity: 'low',
  wordpress_check_core: true,
  wordpress_check_plugins: true,
  wordpress_check_themes: true,
  wordpress_aggressive_detection: false,
  wordpress_enumerate_users: false,
  wordpress_check_config: true,
  wordpress_ports: '80,443,8080,8443',

  ports: '',
  threads: 10,
  timeout: 5,

  // Main Port Discovery configuration
  top_ports: 3000,          // Default: scan top 3000 ports (includes port 3000)
  additional_ports: '',     // Additional ports - leave empty to use only --top-ports

  // UDP Scanning configuration
  enable_udp_scan: true,    // Enable UDP port scanning by default
  udp_top_ports: 100,       // Default: scan top 100 UDP ports
  udp_custom_ports: '53,67,68,69,123,137,138,161,162,500,514,520,623,1194,1434,1900,4500,5060,5353,11211',     // Security-relevant UDP ports

  // Screenshot-specific options - Updated based on screenshots
  screenshot_viewport: true,  // Changed to true (Viewport mode selected in screenshot)
  screenshot_ports: 'default',
  screenshot_custom_ports: '',
  screenshot_timeout: 30,

  // Advanced nmap options - Updated based on screenshots and task requirements
  stealth_scan: true,
  ping_scan: false,    // Changed to false - "No ping" should be default
  script_scan: true,   // Changed to true (NSE Script Scanning enabled in screenshot)
  vulners_script: true, // Changed to true (Vulners Script enabled in screenshot)
  http_cookie_flags: true, // Changed to true (HTTP Cookie Flags Analysis should be enabled by default)
  intense_scan: false,
  aggressive_timing: false,
  no_ping: true,       // Changed to true - "No ping" should be default
  fragment_packets: false,
  decoy_scan: false,
  version_intensity: 5,
  custom_args: '',
  custom_args_all_scans: true, // Default to true - apply custom args to all nmap scans (needed for -e eth1)

  // Vulnerability scanning options - ENHANCED
  vuln_enabled: true,
  vuln_sources: 'cve,custom',
  vuln_min_severity: 'medium',
  vuln_match_mode: 'strict',
  vuln_include_expired: false,
  vuln_custom_rules_only: false,
  enable_exploitdb_correlation: false,

  // Logging and output options
  enable_logging: true,
  log_level: 'info',
  save_raw_output: false,

  // Performance and resource limits
  max_concurrent_hosts: 1,
  max_scan_time: 3600,

  // Web scanning specific options
  web_max_pages: 100,
  web_follow_redirects: true,
  web_user_agent: 'VulnScanner/2.0',
  web_timeout: 15,
  web_quick_mode: false,

  // Share enumeration options
  shares_timeout: 15,
  shares_deep_scan: false,
  shares_test_write: true,

  // SSL/TLS scanning options
  ssl_test_all_ciphers: false,
  ssl_check_certs: true,
  ssl_test_compression: true,

  // Output and reporting options
  export_formats: 'html,json',
  include_screenshots: true,
  report_template: 'default',

  // OSINT scanning configuration - Updated based on screenshots
  osint_enabled: true,  // Changed to true (OSINT enabled in screenshot)
  osint_subdomain_discovery: false, // Changed to false (UNCHECKED in screenshot)
  osint_wildcard_detection: true,   // Wildcard Detection is CHECKED in screenshot
  osint_create_targets: true,      // Auto-Create Targets is CHECKED in screenshot
  osint_max_subdomains: 1000,
  osint_timeout: 300,
  osint_rate_limit: 10,
  osint_sources: ['certspotter', 'shodan', 'urlscan', 'otx', 'hackertarget', 'sitedossier', 'rapiddns', 'subdomaincenter'], // Added missing sources from screenshot
  osint_passive_only: false, // Changed to false (UNCHECKED in screenshot)
  osint_include_expired: false,
  osint_min_confidence: 70,
  osint_dns_brute_force: false,
  osint_brute_wordlist: 'default',
  osint_ptr_analysis: true,
  osint_zone_transfer: false,
  osint_certificate_transparency: true, // Certificate Transparency is CHECKED in screenshot
  osint_search_engines: true,           // Search Engines is CHECKED in screenshot
  osint_social_media: false,
  osint_code_repositories: true,
  osint_threat_intelligence: true,
  osint_custom_apis: [],
  osint_output_format: 'json',
  osint_save_raw_data: false,

  // Nuclei engine configuration - Updated based on screenshots
  nuclei_enabled: true, // Nuclei scanning should be enabled by default
  nuclei_templates: [],
  nuclei_categories: ['http', 'network', 'dns'],
  nuclei_severities: ['medium', 'high', 'critical'],
  nuclei_tags: [],
  nuclei_concurrency: 25,
  nuclei_rate_limit: 150,
  nuclei_use_detected_tech: true,
  nuclei_custom_templates: false,
  nuclei_template_path: '',
  nuclei_exclude_templates: [],
  nuclei_include_templates: [],
  nuclei_passive_scan: false,
  nuclei_follow_redirects: true,
  nuclei_max_redirects: 10,
  nuclei_timeout: 10,
  nuclei_retries: 1,
  nuclei_bulk_size: 25,
  nuclei_template_threads: 10,
  nuclei_headless: false,
  nuclei_system_resolvers: false,
  nuclei_disable_clustering: false,
  nuclei_offline_scan: false,
  nuclei_scan_all_ips: false,
  nuclei_ip_version: 'auto',
  nuclei_interface: '',
  nuclei_source_ip: '',
  nuclei_config_file: '',
  nuclei_report_config: '',
  nuclei_severity_threshold: 'info',
  nuclei_exclude_severity: [],
  nuclei_exclude_protocols: [],
  nuclei_exclude_tags: [],
  nuclei_include_tags: [],
  nuclei_include_ids: [],
  nuclei_exclude_ids: [],
  nuclei_author: [],
  nuclei_attack_type: [],
  nuclei_protocol: [],
  nuclei_exclude_matchers: [],
  nuclei_include_conditions: [],
  nuclei_stop_at_first_match: false,
  nuclei_exclude_type: [],
  nuclei_include_type: [],
  nuclei_template_display: false,
  nuclei_template_list: false,
  nuclei_silent: false,
  nuclei_verbose: false,
  nuclei_very_verbose: false,
  nuclei_debug: false,
  nuclei_debug_requests: false,
  nuclei_debug_response: false,
  nuclei_proxy_url: '',
  nuclei_proxy_socks_url: '',
  nuclei_custom_headers: [],
  nuclei_custom_attack_type: '',
  nuclei_random_agent: false,
  nuclei_disable_redirects: false,
  nuclei_report_format: ['json'],
  nuclei_report_db: '',
  nuclei_markdown_export: '',
  nuclei_sarif_export: '',
  nuclei_json_export: '',
  nuclei_jsonl_export: '',
  nuclei_junit_export: '',
  nuclei_csv_export: '',
  nuclei_disk_export: '',
  nuclei_resume: '',
  nuclei_stream_logs: false,
  nuclei_no_meta: false,
  nuclei_no_timestamp: false,
  nuclei_env_vars: false,
  nuclei_max_host_error: 30,
  nuclei_track_error: [],
  nuclei_flush_interval: 10,
  nuclei_stats_interval: 5,
  nuclei_metrics: false,
  nuclei_metrics_port: 9092,
  nuclei_no_color: false,
  nuclei_no_update_templates: false,
  nuclei_no_templates: false,
  nuclei_validate: false,
  nuclei_validate_export: '',

  // Advanced Web Vulnerability Scanning
  advanced_web_scan: false,
  sqli_scan: false,
  xss_scan: false,
  open_redirect_scan: false,
  ssrf_scan: false,

  // Network Protocol Scanning
  snmp_scan: true,
  ldap_scan: false,
  database_scan: true,
  container_scan: false,
  default_creds_scan: true, // Enabled by default - tests HTTP Basic Auth, login forms, FTP, VNC, etc.
  ipmi_scan: true,
  rdp_scan: true,
  protocol_sec_scan: true,
  solr_scan: true,
  clamav_scan: true,
  php_version_scan: true,
  os_eol_scan: true,

  // Additional Web Security Scanning
  cms_scan: false,
  jwt_scan: false,
  secrets_scan: false,

  // ICS/OT Scanning
  ot_scan: false,

  // Hostname alias scanning behaviour (default parallel)
  alias_scan_sequential: false,

  // OAST (Out-of-Band Application Security Testing)
  oast_enabled: true,
  oast_server_url: 'oast.exposiq.ch',

  // Active Exploit Validation (INTRUSIVE - disabled by default)
  exploit_validation_enabled: true,
  exploit_validation_mode: 'safe', // safe = exclude dangerous exploits, full = all exploits
  dangerous_exploit_consent: false,
  dangerous_exploit_consent_at: undefined,
  dangerous_exploit_consent_by: undefined,

  // API Vulnerability Scanning (disabled by default)
  api_scan_enabled: false,
  api_openapi_url: '',
  api_auth_token: '',
  api_auth_header: 'Authorization',
  api_base_path: '',
  api_graphql_enabled: true,
  api_rate_limit_test: false,
};

export const SCAN_TEMPLATES: ScanTemplate[] = [
  {
    id: 'external',
    icon: 'fa-globe',
    color: '#3b82f6',
    titleKey: 'scanTemplates.external.title',
    descriptionKey: 'scanTemplates.external.description',
    highlightKeys: [
      'scanTemplates.external.highlight1',
      'scanTemplates.external.highlight2',
      'scanTemplates.external.highlight3',
      'scanTemplates.external.highlight4',
    ],
    configOverrides: {
      // Fast engines ON (all <60s, high value for external)
      secrets_scan: true,       // ~30-60s — exposed API keys/creds critical externally
      jwt_scan: true,           // ~20-60s — REST APIs exist on external targets too
      // Already ON in defaults: ssl, dns, web, screenshots, retirejs, wordpress,
      // protocol_sec, snmp, default_creds, rdp, nuclei, exploit_validation, oast
    },
  },
  {
    id: 'internal',
    icon: 'fa-building',
    color: '#059669',
    titleKey: 'scanTemplates.internal.title',
    descriptionKey: 'scanTemplates.internal.description',
    highlightKeys: [
      'scanTemplates.internal.highlight1',
      'scanTemplates.internal.highlight2',
      'scanTemplates.internal.highlight3',
      'scanTemplates.internal.highlight4',
    ],
    configOverrides: {
      // Stealth not needed internally, aggressive timing OK
      stealth_scan: false,
      aggressive_timing: true,
      // Internal-specific engines
      shares_enum: true,        // SMB/NFS share discovery
      shares_deep_scan: true,   // Deep share analysis
      ldap_scan: true,          // AD/LDAP scanning
      container_scan: true,     // Docker/K8s detection
      cms_scan: true,           // Internal CMS instances
      ot_scan: true,            // ICS/OT devices on internal networks
      // Fast engines ON (same as external)
      secrets_scan: true,       // ~30-60s — credential exposure
      jwt_scan: true,           // ~20-60s — internal APIs
    },
  },
  {
    id: 'recon',
    icon: 'fa-camera-retro',
    color: '#0ea5e9',
    titleKey: 'scanTemplates.recon.title',
    descriptionKey: 'scanTemplates.recon.description',
    highlightKeys: [
      'scanTemplates.recon.highlight1',
      'scanTemplates.recon.highlight2',
      'scanTemplates.recon.highlight3',
    ],
    configOverrides: {
      // Lightweight recon basis for manual pentest: ONLY screenshots + share
      // enumeration. No full port/service/OS scan — each module probes just the
      // ports it needs (web-port list for screenshots, SMB/NFS/RPC/rsync for shares).
      // Everything else is explicitly OFF (many engines are ON by default).
      screenshots: true,
      shares_enum: true,
      // Core port/service/OS
      port_scan: false,
      service_detection: false,
      os_detection: false,
      stealth_scan: false,       // SYN-scan modality — irrelevant without a port scan
      enable_udp_scan: false,
      script_scan: false,
      vulners_script: false,
      http_cookie_flags: false,
      // Web / vuln / nuclei
      web_scan: false,
      ssl_scan: false,
      vuln_scan: false,
      vuln_enabled: false,
      retirejs_scan: false,
      wordpress_scan: false,
      nuclei_enabled: false,
      advanced_web_scan: false,
      sqli_scan: false,
      xss_scan: false,
      open_redirect_scan: false,
      ssrf_scan: false,
      cms_scan: false,
      jwt_scan: false,
      secrets_scan: false,
      // DNS / OSINT
      dns_scan: false,
      osint_scan: false,
      osint_enabled: false,
      // Network-protocol engines (ON by default)
      snmp_scan: false,
      ldap_scan: false,
      database_scan: false,
      container_scan: false,
      default_creds_scan: true, // product-aware default-credential testing (fingerprint → vendor creds, form-login + Basic-auth)
      ipmi_scan: false,
      rdp_scan: false,
      protocol_sec_scan: false,
      solr_scan: false,
      clamav_scan: false,
      php_version_scan: false,
      os_eol_scan: false,
      ot_scan: false,
      // OAST / exploit validation / API (ON by default)
      oast_enabled: false,
      exploit_validation_enabled: false,
      api_scan_enabled: false,
    },
  },
  {
    id: 'webapp',
    icon: 'fa-code',
    color: '#7c3aed',
    titleKey: 'scanTemplates.webapp.title',
    descriptionKey: 'scanTemplates.webapp.description',
    highlightKeys: [
      'scanTemplates.webapp.highlight1',
      'scanTemplates.webapp.highlight2',
      'scanTemplates.webapp.highlight3',
      'scanTemplates.webapp.highlight4',
    ],
    configOverrides: {
      // Active web vulnerability testing (slow but essential for webapp profile)
      advanced_web_scan: true,  // Master toggle
      sqli_scan: true,          // SQL injection testing
      xss_scan: true,           // Cross-site scripting
      ssrf_scan: true,          // Server-side request forgery
      open_redirect_scan: true, // Open redirect testing
      api_scan_enabled: true,   // API vulnerability scanning
      // Fast web-focused engines
      jwt_scan: true,           // JWT token analysis
      secrets_scan: true,       // API key/credential detection
      cms_scan: true,           // CMS detection
      wordpress_aggressive_detection: true,
      wordpress_enumerate_users: true,
      screenshot_viewport: false, // Full page screenshots for webapp
      // Disable irrelevant infrastructure engines (save time)
      os_detection: false,      // Not relevant for web apps
      enable_udp_scan: false,   // Web apps don't use UDP
      snmp_scan: false,         // Infrastructure only
      ipmi_scan: false,         // Infrastructure only
      rdp_scan: false,          // Infrastructure only
      solr_scan: false,         // Infrastructure only
      clamav_scan: false,       // Infrastructure only
      dns_scan: false,          // Not webapp-relevant
      // Keep protocol_sec ON — detects cleartext on any port (<3s)
      top_ports: 1000,          // Fewer ports, focus on web
      threads: 5,               // Lower parallelism, deeper per-target
    },
  },
];

// Fast agent scan template — shown only when an agent is selected.
// Optimized for speed: fewer ports, quick web mode, skip slow engines.
// Only disables engines that take >2 min per target (OSINT, advanced_web, exploit validation).
// Fast engines (<30s each) like IPMI, Solr, ClamAV, LDAP, secrets, jwt, cms stay ON.
export const FAST_AGENT_TEMPLATE: ScanTemplate = {
  id: 'fast_agent',
  icon: 'fa-bolt',
  color: '#ef4444',
  titleKey: 'scanTemplates.fastAgent.title',
  descriptionKey: 'scanTemplates.fastAgent.description',
  highlightKeys: [
    'scanTemplates.fastAgent.highlight1',
    'scanTemplates.fastAgent.highlight2',
    'scanTemplates.fastAgent.highlight3',
    'scanTemplates.fastAgent.highlight4',
  ],
  configOverrides: {
    top_ports: 1000,
    enable_udp_scan: false,
    web_quick_mode: true,
    // Disable slow engines (>2 min per target)
    sqli_scan: false,
    xss_scan: false,
    advanced_web_scan: false,
    osint_scan: false,
    exploit_validation_enabled: false,
    // Keep fast engines ON — each <30s per target, high value
    // ipmi_scan, solr_scan, clamav_scan, container_scan, ldap_scan,
    // secrets_scan, cms_scan, jwt_scan, ot_scan all stay at defaults
  },
};

// Change Detection Monitor — lightweight continuous monitoring profile.
// Runs only essential detection engines to spot infrastructure changes (new ports, expired certs, DNS changes).
// Designed for scheduled daily/weekly scans to track change over time without heavy scanning.
export const MONITOR_TEMPLATE: ScanTemplate = {
  id: 'monitor',
  icon: 'fa-satellite-dish',
  color: '#06b6d4',
  titleKey: 'scanTemplates.monitor.title',
  descriptionKey: 'scanTemplates.monitor.description',
  highlightKeys: [
    'scanTemplates.monitor.highlight1',
    'scanTemplates.monitor.highlight2',
    'scanTemplates.monitor.highlight3',
    'scanTemplates.monitor.highlight4',
  ],
  configOverrides: {
    // Lightweight port scan
    top_ports: 100,
    enable_udp_scan: false,
    aggressive_timing: true,
    // Core detection only
    screenshots: true,        // Visual change detection
    ssl_scan: true,           // Certificate expiry monitoring
    dns_scan: true,           // DNS record changes
    web_scan: true,           // Basic web header checks
    web_quick_mode: true,     // Quick mode (~40 key paths)
    // Disable everything heavy
    nuclei_enabled: false,
    exploit_validation_enabled: false,
    advanced_web_scan: false,
    sqli_scan: false,
    xss_scan: false,
    ssrf_scan: false,
    open_redirect_scan: false,
    osint_scan: false,
    retirejs_scan: false,
    wordpress_scan: false,
    vuln_scan: false,
    shares_enum: false,
    container_scan: false,
    ldap_scan: false,
    cms_scan: false,
    jwt_scan: false,
    secrets_scan: false,
    ot_scan: false,
    oast_enabled: false,
    ipmi_scan: false,
    clamav_scan: false,
    solr_scan: false,
    database_scan: false,
    snmp_scan: false,
    default_creds_scan: false,
    rdp_scan: false,
    protocol_sec_scan: false,
    api_scan_enabled: false,
  },
};

// Quick Scan template — fastest possible scan with only essential checks.
// Finishes in 2-5 minutes per target. Good for initial reconnaissance.
export const QUICK_SCAN_TEMPLATE: ScanTemplate = {
  id: 'quick',
  icon: 'fa-forward',
  color: '#f59e0b',
  titleKey: 'scanTemplates.quick.title',
  descriptionKey: 'scanTemplates.quick.description',
  highlightKeys: [
    'scanTemplates.quick.highlight1',
    'scanTemplates.quick.highlight2',
    'scanTemplates.quick.highlight3',
    'scanTemplates.quick.highlight4',
  ],
  configOverrides: {
    top_ports: 100,
    enable_udp_scan: false,
    web_quick_mode: true,
    screenshots: false,
    wordpress_scan: false,
    retirejs_scan: false,
    osint_scan: false,
    nuclei_enabled: false,
    exploit_validation_enabled: false,
    advanced_web_scan: false,
    sqli_scan: false,
    xss_scan: false,
    ssrf_scan: false,
    open_redirect_scan: false,
    shares_enum: false,
    container_scan: false,
    ldap_scan: false,
    cms_scan: false,
    jwt_scan: false,
    secrets_scan: false,
    ot_scan: false,
    oast_enabled: false,
    ipmi_scan: false,
    clamav_scan: false,
    solr_scan: false,
    database_scan: false,
    snmp_scan: false,
  },
};

// Saved scan profile from the database
export interface ScanProfile {
  id: number;
  name: string;
  description: string;
  config: string; // JSON-serialized ScanConfig
  is_default: boolean;
  created_by_id: number;
  created_at: string;
  updated_at: string;
}
